import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import WholesaleLayout, { useWholesaleAuth } from './WholesaleLayout';
import { getWholesaleOrder, editWholesaleOrder, getWholesaleProducts } from '../../lib/api';

export default function WholesaleAdminEditOrder() {
  const user = useWholesaleAuth('admin');
  const { oid } = useParams();
  const navigate = useNavigate();
  const [order, setOrder] = useState(null);
  const [items, setItems] = useState([]);
  const [editNote, setEditNote] = useState('');
  const [products, setProducts] = useState([]);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!user) return;
    getWholesaleOrder(oid).then(r => {
      setOrder(r.data);
      setItems((r.data.items || []).map((it, i) => ({ ...it, _key: i })));
      setEditNote('');
    }).catch(() => {});
    getWholesaleProducts().then(r => setProducts(r.data)).catch(() => {});
  }, [user, oid]);

  const stockMap = {};
  products.forEach(p => { stockMap[p.id] = p.stock ?? 0; });

  const updateItem = (idx, field, value) => {
    setItems(prev => prev.map((it, i) => i === idx ? { ...it, [field]: value } : it));
  };

  const removeItem = (idx) => {
    if (items.length <= 1) return;
    setItems(prev => prev.filter((_, i) => i !== idx));
  };

  const addEmpty = () => setItems(prev => [...prev, { id: '', name: '', price: 0, qty: 1, hsn: '91021100', _key: Date.now() }]);

  const addFromCatalogue = (p) => {
    setItems(prev => [...prev, { id: p.id, name: `${p.model} - ${p.color}`, price: p.price, qty: 1, hsn: p.hsn || '91021100', _key: Date.now() + Math.random() }]);
  };

  const grandTotal = items.reduce((s, it) => s + (parseFloat(it.price) || 0) * (parseInt(it.qty) || 0), 0);

  const handleSave = async () => {
    setSaving(true);
    try {
      const cleanItems = items.map(({ _key, ...rest }) => ({
        ...rest,
        price: parseFloat(rest.price) || 0,
        qty: parseInt(rest.qty) || 0,
      }));
      await editWholesaleOrder(oid, { items: cleanItems, edit_note: editNote });
      navigate('/wholesale/admin/orders');
    } catch (e) { alert(e.response?.data?.detail || 'Save failed'); }
    setSaving(false);
  };

  if (!order) return <WholesaleLayout user={user} title="Edit Order"><div style={{ color:'#888' }}>Loading...</div></WholesaleLayout>;

  return (
    <WholesaleLayout user={user} title={`Edit Order ${oid}`}>
      {order.admin_edited && (
        <div style={{ background:'#FFF3E0', border:'1px solid #FFB74D', borderRadius:4, padding:'12px 16px', marginBottom:20, fontSize:13, color:'#E65100' }}>
          This order has been previously edited by admin. {order.edit_note && <span>Note: {order.edit_note}</span>}
        </div>
      )}

      <div style={card}>
        <div style={cardTitle}>Order Items</div>
        <table style={{ width:'100%', borderCollapse:'collapse', fontSize:13 }}>
          <thead>
            <tr>{['Item','Unit Price','Qty','HSN','Line Total',''].map(h => <th key={h} style={thStyle}>{h}</th>)}</tr>
          </thead>
          <tbody>
            {items.map((it, idx) => {
              const lineTotal = (parseFloat(it.price) || 0) * (parseInt(it.qty) || 0);
              const currentStock = it.id ? stockMap[it.id] : undefined;
              return (
                <tr key={it._key}>
                  <td style={tdStyle}>
                    <input value={it.name || ''} onChange={e => updateItem(idx, 'name', e.target.value)} style={cellInput} />
                    {currentStock !== undefined && <div style={{ fontSize:10, color:'#888', marginTop:2 }}>(stock: {currentStock})</div>}
                  </td>
                  <td style={tdStyle}><input type="number" value={it.price} onChange={e => updateItem(idx, 'price', e.target.value)} style={{ ...cellInput, width:100 }} /></td>
                  <td style={tdStyle}><input type="number" min="1" value={it.qty} onChange={e => updateItem(idx, 'qty', e.target.value)} style={{ ...cellInput, width:70 }} /></td>
                  <td style={tdStyle}><input value={it.hsn || ''} onChange={e => updateItem(idx, 'hsn', e.target.value)} style={{ ...cellInput, width:100 }} /></td>
                  <td style={tdStyle}><strong>${lineTotal.toLocaleString()}</strong></td>
                  <td style={tdStyle}>{items.length > 1 && <button onClick={() => removeItem(idx)} style={{ background:'none', border:'none', cursor:'pointer', color:'#C41E2A', fontSize:16, fontWeight:700 }}>&times;</button>}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
        <button onClick={addEmpty} style={{ ...btnGhost, marginTop:12 }}>+ Add Line</button>
        <div style={{ marginTop:16, textAlign:'right', fontFamily:"'Barlow Condensed',sans-serif", fontSize:24, fontWeight:700 }}>
          Grand Total: ${grandTotal.toLocaleString()}
        </div>
      </div>

      <div style={{ ...card, marginTop:20 }}>
        <div style={cardTitle}>Quick Add from Catalogue</div>
        <div style={{ display:'flex', flexWrap:'wrap', gap:8 }}>
          {products.map(p => (
            <button key={p.id} onClick={() => addFromCatalogue(p)}
              style={{ padding:'6px 12px', fontSize:11, fontFamily:'Barlow', fontWeight:500, border:'1px solid #E0DED8', borderRadius:2, background:'#fff', cursor:'pointer' }}>
              {p.model} - {p.color} (${p.price}) <span style={{ color:'#888', fontSize:10 }}>stk:{p.stock??0}</span>
            </button>
          ))}
          {products.length === 0 && <span style={{ color:'#888', fontSize:12 }}>No products in catalogue</span>}
        </div>
      </div>

      <div style={{ ...card, marginTop:20 }}>
        <label style={labelStyle}>Edit Note</label>
        <input value={editNote} onChange={e => setEditNote(e.target.value)} placeholder="Describe changes made" style={inputStyle} data-testid="edit-note-input" />
      </div>

      <div style={{ marginTop:24, display:'flex', gap:12 }}>
        <button onClick={handleSave} disabled={saving} data-testid="save-edit-btn" style={btnPrimary}>{saving ? 'Saving...' : 'Save Changes'}</button>
        <button onClick={() => navigate('/wholesale/admin/orders')} style={btnGhost}>Cancel</button>
      </div>
    </WholesaleLayout>
  );
}

const card = { background:'#fff', border:'1px solid #E0DED8', borderRadius:4, padding:24 };
const cardTitle = { fontFamily:"'Barlow Condensed',sans-serif", fontSize:16, fontWeight:700, letterSpacing:1.5, textTransform:'uppercase', marginBottom:16 };
const thStyle = { padding:'10px 12px', fontSize:10, letterSpacing:1.5, textTransform:'uppercase', color:'#888', borderBottom:'2px solid #E0DED8', fontWeight:600, textAlign:'left' };
const tdStyle = { padding:'8px 12px', borderBottom:'1px solid #E0DED8', verticalAlign:'middle' };
const cellInput = { width:'100%', padding:'8px 10px', border:'1px solid #E0DED8', borderRadius:2, fontFamily:'Barlow', fontSize:13, outline:'none' };
const labelStyle = { display:'block', fontSize:11, letterSpacing:1.5, textTransform:'uppercase', fontWeight:600, color:'#888', marginBottom:6 };
const inputStyle = { width:'100%', padding:'10px 12px', border:'1px solid #E0DED8', borderRadius:2, fontFamily:'Barlow', fontSize:13, outline:'none' };
const btnPrimary = { display:'inline-flex', alignItems:'center', gap:6, padding:'8px 16px', fontFamily:'Barlow', fontSize:12, fontWeight:600, letterSpacing:1, textTransform:'uppercase', borderRadius:2, cursor:'pointer', background:'#C41E2A', color:'#fff', border:'none' };
const btnGhost = { display:'inline-flex', alignItems:'center', gap:6, padding:'8px 16px', fontFamily:'Barlow', fontSize:12, fontWeight:600, letterSpacing:1, textTransform:'uppercase', borderRadius:2, cursor:'pointer', background:'transparent', color:'#888', border:'1px solid #E0DED8' };
